import React, {useCallback, useRef, useEffect, useState} from 'react'
import {Modal, ModalHeader, ModalBody, Button, Spinner} from 'reactstrap'
import {useBackgroundRemoval} from '../../hooks/useBackgroundRemoval'

/**
 * UI-only modal for removing the background from an image.
 * Previews the result and emits onApply(blob) — no upload, no dispatch, no runtime.
 *
 * @param {{ isOpen: boolean, toggle: () => void, imageSrc: string|null, onApply: (blob: Blob) => void }} props
 */
function RemoveBgModal({isOpen, toggle, imageSrc, onApply}) {
    const runWhen = isOpen && !!imageSrc
    const {status, blob, error, retry} = useBackgroundRemoval(imageSrc, {runWhen})
    const applyBtnRef = useRef(null)
    const [previewUrl, setPreviewUrl] = useState(null)

    useEffect(() => {
        if (status === 'preview' && applyBtnRef.current) {
            applyBtnRef.current.focus()
        }
    }, [status])

    useEffect(() => {
        if (blob && status === 'preview') {
            const url = URL.createObjectURL(blob)
            setPreviewUrl(url)
            return () => {
                URL.revokeObjectURL(url)
                setPreviewUrl(null)
            }
        } else {
            setPreviewUrl(null)
        }
    }, [blob, status])

    const handleApply = useCallback(() => {
        if (!blob) return
        onApply(blob)
        toggle()
    }, [blob, onApply, toggle])

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                toggle()
                return
            }
            if (e.key === 'Enter' && status === 'preview') {
                e.preventDefault()
                handleApply()
            }
        },
        [status, handleApply, toggle],
    )

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="CreateCustomShape__RemoveBgModal" onKeyDown={handleKeyDown}>
            <ModalHeader toggle={toggle}>Remove Background</ModalHeader>
            <ModalBody>
                <div className="RemoveBgModal__PreviewWrap">
                    {(status === 'processing' || (status === 'idle' && imageSrc)) && (
                        <div className="RemoveBgModal__Loading">
                            <Spinner color="primary" />
                            <p className="RemoveBgModal__LoadingText">Processing image...</p>
                        </div>
                    )}
                    {status === 'preview' && previewUrl && (
                        <div className="RemoveBgModal__Preview RemoveBgModal__Preview--checkerboard">
                            <img src={previewUrl} alt="Preview" className="RemoveBgModal__PreviewImg" />
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="RemoveBgModal__Error">
                            <p className="RemoveBgModal__ErrorText">Failed to remove background</p>
                            {error?.message && (
                                <p className="RemoveBgModal__ErrorDetail text-muted small mt-1">{error.message}</p>
                            )}
                            {error?.message && /fetch|404|model|onnx|cdn/i.test(error.message) && (
                                <p className="RemoveBgModal__ErrorHint small mt-2">
                                    Model is loaded from CDN. Check your network or try again later.
                                </p>
                            )}
                            <div className="RemoveBgModal__ErrorActions">
                                <Button color="primary" outline onClick={retry}>
                                    Retry
                                </Button>
                                <Button color="secondary" outline onClick={toggle}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                {status !== 'error' && (
                    <div className="RemoveBgModal__Actions">
                        <Button color="secondary" outline onClick={toggle}>
                            Cancel
                        </Button>
                        <Button ref={applyBtnRef} color="primary" disabled={status !== 'preview'} onClick={handleApply}>
                            Apply
                        </Button>
                    </div>
                )}
            </ModalBody>
        </Modal>
    )
}

export default RemoveBgModal
